"use client";

import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, User, Shield, Bell, Palette, Loader2, CheckCircle2, Save, Key, Globe, Monitor, Sun, Moon, Laptop, Type, Maximize2, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/auth-context";
import { useTheme, type ThemeMode, type SidebarDensity, type AccentColor } from "@/contexts/theme-context";

interface UserSettingsDialogProps { open: boolean; onClose: () => void; }

function getToken() { return localStorage.getItem("ts_token") || ""; }
function apiFetch(path: string, method = "GET", body?: any) {
  const opts: RequestInit = { method, headers: { "Content-Type": "application/json", Authorization: getToken() } };
  if (body) opts.body = JSON.stringify(body);
  return fetch("/api" + path, opts).then(r => r.json());
}

export function UserSettingsDialog({ open, onClose }: UserSettingsDialogProps) {
  const { user, tenant } = useAuth();
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState("profile");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [formData, setFormData] = useState({ displayName: "", email: "", phone: "", avatar: "" });
  const [pwOld, setPwOld] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState({ system: true, approval: true, alert: true, mail: true });

  // Local copies for appearance (apply on save)
  const [localThemeMode, setLocalThemeMode] = useState<ThemeMode>("light");
  const [localDensity, setLocalDensity] = useState<SidebarDensity>("comfortable");
  const [localAccent, setLocalAccent] = useState<AccentColor>("gold");
  const [localFontSize, setLocalFontSize] = useState<"small" | "medium" | "large">("medium");
  const [localCompactNav, setLocalCompactNav] = useState(false);

  // Sync local appearance from context when dialog opens
  useEffect(() => {
    if (open) {
      setLocalThemeMode(theme.themeMode);
      setLocalDensity(theme.sidebarDensity);
      setLocalAccent(theme.accentColor);
      setLocalFontSize(theme.fontSize);
      setLocalCompactNav(theme.compactNav);
    }
  }, [open]);

  // Load user data when dialog opens
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    apiFetch("/auth/info").then(res => {
      if (res.code === 200 && res.data?.user) {
        const u = res.data.user;
        setFormData({
          displayName: u.displayName || u.display_name || u.username || "",
          email: u.email || "",
          phone: u.phone || "",
          avatar: u.avatar || ""
        });
      }
    }).catch(() => {
      if (user) setFormData({ displayName: user.displayName || "", email: user.email || "", phone: user.phone || "", avatar: user.avatar || "" });
    }).finally(() => setLoading(false));
  }, [open, user]);

  // ESC key to close
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const res = await apiFetch("/auth/profile", "PUT", formData);
      showToast(res.code === 200 ? "个人信息保存成功" : "保存失败: " + (res.msg || "未知错误"));
    } catch { showToast("保存失败，请重试"); }
    setSaving(false);
  };

  const changePassword = async () => {
    if (!pwOld || !pwNew) { showToast("请填写完整密码信息"); return; }
    if (pwNew.length < 6) { showToast("新密码至少6位"); return; }
    if (pwNew !== pwConfirm) { showToast("两次输入的新密码不一致"); return; }
    setPwSaving(true);
    try {
      const res = await apiFetch("/auth/password", "PUT", { oldPassword: pwOld, newPassword: pwNew });
      if (res.code === 200) {
        showToast("密码修改成功");
        setPwOld(""); setPwNew(""); setPwConfirm("");
      } else {
        showToast("修改失败: " + (res.msg || "原密码错误"));
      }
    } catch { showToast("修改失败，请重试"); }
    setPwSaving(false);
  };

  // Save appearance settings to context (takes effect immediately)
  const saveAppearance = () => {
    theme.setThemeMode(localThemeMode);
    theme.setSidebarDensity(localDensity);
    theme.setAccentColor(localAccent);
    theme.setFontSize(localFontSize);
    theme.setCompactNav(localCompactNav);
    showToast("外观设置已保存");
  };

  const tabs = [
    { id: "profile", label: "个人信息", icon: User },
    { id: "security", label: "安全设置", icon: Shield },
    { id: "appearance", label: "外观设置", icon: Palette },
    { id: "notification", label: "通知偏好", icon: Bell },
    { id: "language", label: "语言与地区", icon: Globe },
    { id: "sessions", label: "登录设备", icon: Monitor },
  ];

  if (!open) return null;

  const accentColors: { id: AccentColor; label: string; color: string }[] = [
    { id: "gold", label: "金色", color: "#b8943f" },
    { id: "blue", label: "蓝色", color: "#2563eb" },
    { id: "violet", label: "紫色", color: "#7c3aed" },
    { id: "cyan", label: "青色", color: "#0891b2" },
    { id: "green", label: "绿色", color: "#059669" },
  ];

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" style={{position:"fixed",top:0,left:0,right:0,bottom:0}}>
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      {toast && <div className={cn("fixed top-4 right-4 z-[10100] flex items-center gap-2 px-4 py-2 rounded-lg shadow-lg text-sm", toast.includes("成功") ? "bg-green-600 text-white" : "bg-red-600 text-white")}>{toast.includes("成功") ? <CheckCircle2 className="w-4 h-4" /> : null}{toast}</div>}
      <div className="relative w-[760px] max-h-[85vh] bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-border/50 flex overflow-hidden">
        {/* Left sidebar */}
        <div className="w-44 bg-gray-50 dark:bg-slate-800 border-r border-border/50 flex flex-col">
          <div className="p-4 border-b border-border/30 flex items-center justify-between">
            <h3 className="text-sm font-semibold">个人设置</h3>
            <button onClick={onClose} className="w-6 h-6 rounded-full hover:bg-gray-200 dark:hover:bg-slate-700 flex items-center justify-center transition-colors">
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
          <nav className="flex-1 p-2 space-y-0.5">
            {tabs.map(tab => { const Icon = tab.icon; return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={cn("w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] transition-colors", activeTab === tab.id ? "bg-[#2563EB] text-white" : "text-muted-foreground hover:bg-accent hover:text-foreground")}>
                <Icon className="w-4 h-4" />{tab.label}
              </button>); })}
          </nav>
        </div>
        {/* Right content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6">
            {loading ? <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#2563EB]" /></div> :
            <>
              {/* === 个人信息 === */}
              {activeTab === "profile" && (
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#2563EB]/30 to-[#2563EB]/10 border-2 border-[#2563EB]/20 flex items-center justify-center text-2xl font-bold text-[#2563EB]">{(formData.displayName || "?").charAt(0)}</div>
                    <div>
                      <h3 className="text-lg font-semibold">{formData.displayName || "用户"}</h3>
                      <p className="text-sm text-muted-foreground">{user?.username} · {tenant?.name || "系统管理"}</p>
                      <Badge variant="secondary" className="text-xs mt-1">ID: {user?.id}</Badge>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><label className="text-sm font-medium">显示名称</label><input type="text" value={formData.displayName} onChange={e => setFormData({...formData, displayName: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-border/50 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30" /></div>
                    <div className="space-y-2"><label className="text-sm font-medium">邮箱</label><input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-border/50 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30" /></div>
                    <div className="space-y-2"><label className="text-sm font-medium">手机号</label><input type="tel" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-border/50 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30" /></div>
                    <div className="space-y-2"><label className="text-sm font-medium">所属租户</label><input type="text" value={tenant?.name || "系统管理"} disabled className="w-full px-3 py-2 rounded-lg border border-border/50 bg-gray-50 dark:bg-slate-800 text-sm text-muted-foreground cursor-not-allowed" /></div>
                  </div>
                </div>
              )}

              {/* === 安全设置 === */}
              {activeTab === "security" && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-sm font-medium flex items-center gap-2 mb-3"><Key className="w-4 h-4 text-[#2563EB]" />修改密码</h3>
                    <div className="space-y-3 p-4 rounded-lg border border-border/50 bg-gray-50/50 dark:bg-slate-800/50">
                      <input type="password" placeholder="当前密码" value={pwOld} onChange={e => setPwOld(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-border/50 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30" />
                      <input type="password" placeholder="新密码（至少6位）" value={pwNew} onChange={e => setPwNew(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-border/50 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30" />
                      <input type="password" placeholder="确认新密码" value={pwConfirm} onChange={e => setPwConfirm(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-border/50 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30" />
                      <Button size="sm" className="bg-[#2563EB] hover:bg-[#2563EB]/90 text-white" onClick={changePassword} disabled={pwSaving}>
                        {pwSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}更新密码
                      </Button>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium mb-3">登录历史</h3>
                    <div className="p-4 rounded-lg border border-border/50 text-sm text-muted-foreground">
                      <p>最近登录: {user?.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString("zh-CN") : "暂无记录"}</p>
                      <p className="mt-1">登录IP: {user?.lastLoginIp || "未知"}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* === 外观设置 (ENHANCED) === */}
              {activeTab === "appearance" && (
                <div className="space-y-6">
                  {/* Theme Mode */}
                  <div>
                    <h3 className="text-sm font-medium flex items-center gap-2 mb-3"><Sun className="w-4 h-4 text-[#2563EB]" />主题模式</h3>
                    <div className="grid grid-cols-3 gap-3">
                      {([
                        { id: "light" as ThemeMode, label: "明亮", desc: "浅色主题", icon: Sun, preview: "bg-white border border-border" },
                        { id: "dark" as ThemeMode, label: "暗黑", desc: "深色主题", icon: Moon, preview: "bg-slate-800 border border-slate-600" },
                        { id: "system" as ThemeMode, label: "跟随系统", desc: "自动切换", icon: Laptop, preview: "bg-gradient-to-r from-white to-slate-800" },
                      ]).map(t => {
                        const Icon = t.icon;
                        const isActive = localThemeMode === t.id;
                        return (
                          <button key={t.id} onClick={() => setLocalThemeMode(t.id)} className={cn("p-3 rounded-xl border-2 transition-all text-center group", isActive ? "border-[#2563EB] bg-[#2563EB]/5" : "border-border/50 hover:border-[#2563EB]/30")}>
                            <div className={cn("w-full h-10 rounded-lg mb-2 flex items-center justify-center", t.preview)}>
                              <Icon className={cn("w-5 h-5", t.id === "light" ? "text-amber-500" : t.id === "dark" ? "text-blue-300" : "text-gray-400")} />
                            </div>
                            <span className="text-xs font-semibold block">{t.label}</span>
                            <span className="text-[10px] text-muted-foreground">{t.desc}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Accent Color */}
                  <div>
                    <h3 className="text-sm font-medium flex items-center gap-2 mb-3"><Palette className="w-4 h-4 text-[#2563EB]" />主题色</h3>
                    <div className="flex gap-3">
                      {accentColors.map(c => (
                        <button key={c.id} onClick={() => setLocalAccent(c.id)} className={cn("flex flex-col items-center gap-1.5 p-2 rounded-lg transition-all", localAccent === c.id ? "ring-2 ring-offset-2 ring-[#2563EB] bg-white dark:bg-slate-800" : "hover:bg-gray-50 dark:hover:bg-slate-800")}>
                          <div className="w-8 h-8 rounded-full shadow-sm transition-transform hover:scale-110" style={{ backgroundColor: c.color }} />
                          <span className="text-[10px] text-muted-foreground">{c.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Sidebar Density */}
                  <div>
                    <h3 className="text-sm font-medium flex items-center gap-2 mb-3"><Maximize2 className="w-4 h-4 text-[#2563EB]" />侧边栏密度</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <button onClick={() => setLocalDensity("compact")} className={cn("p-4 rounded-xl border-2 transition-all text-center", localDensity === "compact" ? "border-[#2563EB] bg-[#2563EB]/5" : "border-border/50 hover:border-[#2563EB]/30")}>
                        <div className="flex gap-1 justify-center mb-2">
                          <div className="w-6 h-16 rounded bg-gray-200 dark:bg-slate-700 flex flex-col gap-1 pt-1 px-0.5">
                            <div className="w-full h-1 rounded bg-gray-400" />
                            <div className="w-full h-1 rounded bg-gray-300" />
                            <div className="w-full h-1 rounded bg-gray-300" />
                            <div className="w-full h-1 rounded bg-gray-300" />
                          </div>
                          <div className="flex-1 h-16 rounded bg-gray-100 dark:bg-slate-600" />
                        </div>
                        <span className="text-xs font-semibold">紧凑模式</span>
                        <p className="text-[10px] text-muted-foreground mt-0.5">148px · 小图标 · 紧凑间距</p>
                      </button>
                      <button onClick={() => setLocalDensity("comfortable")} className={cn("p-4 rounded-xl border-2 transition-all text-center", localDensity === "comfortable" ? "border-[#2563EB] bg-[#2563EB]/5" : "border-border/50 hover:border-[#2563EB]/30")}>
                        <div className="flex gap-1 justify-center mb-2">
                          <div className="w-8 h-16 rounded bg-gray-200 dark:bg-slate-700 flex flex-col gap-1.5 pt-1.5 px-1">
                            <div className="w-full h-1.5 rounded bg-gray-400" />
                            <div className="w-full h-1.5 rounded bg-gray-300" />
                            <div className="w-full h-1.5 rounded bg-gray-300" />
                            <div className="w-full h-1.5 rounded bg-gray-300" />
                          </div>
                          <div className="flex-1 h-16 rounded bg-gray-100 dark:bg-slate-600" />
                        </div>
                        <span className="text-xs font-semibold">舒适模式</span>
                        <p className="text-[10px] text-muted-foreground mt-0.5">178px · 标准图标 · 宽松间距</p>
                      </button>
                    </div>
                  </div>

                  {/* Font Size */}
                  <div>
                    <h3 className="text-sm font-medium flex items-center gap-2 mb-3"><Type className="w-4 h-4 text-[#2563EB]" />字体大小</h3>
                    <div className="grid grid-cols-3 gap-3">
                      {([
                        { id: "small" as const, label: "小", size: "13px", sample: "紧凑高效" },
                        { id: "medium" as const, label: "中", size: "14px", sample: "默认推荐" },
                        { id: "large" as const, label: "大", size: "15px", sample: "阅读舒适" },
                      ]).map(s => (
                        <button key={s.id} onClick={() => setLocalFontSize(s.id)} className={cn("p-3 rounded-xl border-2 transition-all text-center", localFontSize === s.id ? "border-[#2563EB] bg-[#2563EB]/5" : "border-border/50 hover:border-[#2563EB]/30")}>
                          <span className="block font-bold" style={{ fontSize: s.size }}>{s.label}</span>
                          <span className="text-[10px] text-muted-foreground">{s.size} · {s.sample}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Preview indicator */}
                  <div className="p-3 rounded-lg border border-dashed border-[#2563EB]/30 bg-[#2563EB]/5 text-xs text-muted-foreground">
                    <p>当前预览：主题 <strong className="text-foreground">{localThemeMode === "light" ? "明亮" : localThemeMode === "dark" ? "暗黑" : "跟随系统"}</strong> · 密度 <strong className="text-foreground">{localDensity === "compact" ? "紧凑(148px)" : "舒适(178px)"}</strong> · 字体 <strong className="text-foreground">{localFontSize === "small" ? "小(13px)" : localFontSize === "medium" ? "中(14px)" : "大(15px)"}</strong></p>
                  </div>
                </div>
              )}

              {/* === 通知偏好 === */}
              {activeTab === "notification" && (
                <div className="space-y-4">
                  <h3 className="text-sm font-medium mb-2">通知开关</h3>
                  {[
                    {key:"system" as const, title:"系统通知", desc:"系统升级、维护公告"},
                    {key:"approval" as const, title:"审批通知", desc:"待审批事项提醒"},
                    {key:"alert" as const, title:"告警通知", desc:"安全告警、异常告警"},
                    {key:"mail" as const, title:"邮件通知", desc:"重要事项邮件提醒"},
                  ].map(item => (
                    <div key={item.key} className="flex items-center justify-between p-3 rounded-lg border border-border/50">
                      <div><p className="text-sm font-medium">{item.title}</p><p className="text-xs text-muted-foreground">{item.desc}</p></div>
                      <button onClick={() => setNotifPrefs(prev => ({...prev, [item.key]: !prev[item.key]}))} className={cn("w-10 h-5 rounded-full transition-colors relative", notifPrefs[item.key] ? "bg-[#2563EB]" : "bg-gray-200")}>
                        <div className={cn("absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform", notifPrefs[item.key] ? "translate-x-5" : "translate-x-0.5")} />
                      </button>
                    </div>
                  ))}
                  <Button size="sm" className="bg-[#2563EB] hover:bg-[#2563EB]/90 text-white" onClick={() => showToast("通知偏好已保存")}>保存设置</Button>
                </div>
              )}

              {/* === 语言与地区 === */}
              {activeTab === "language" && (
                <div className="space-y-5">
                  <div className="space-y-2"><label className="text-sm font-medium">显示语言</label>
                    <select className="w-full px-3 py-2 rounded-lg border border-border/50 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30">
                      <option value="zh-CN">简体中文</option><option value="zh-TW">繁體中文</option><option value="en">English</option>
                    </select>
                  </div>
                  <div className="space-y-2"><label className="text-sm font-medium">时区</label>
                    <select className="w-full px-3 py-2 rounded-lg border border-border/50 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30">
                      <option value="Asia/Shanghai">亚洲/上海 (UTC+8)</option><option value="Asia/Tokyo">亚洲/东京 (UTC+9)</option><option value="America/New_York">美洲/纽约 (UTC-5)</option><option value="Europe/London">欧洲/伦敦 (UTC+0)</option>
                    </select>
                  </div>
                  <div className="space-y-2"><label className="text-sm font-medium">日期格式</label>
                    <select className="w-full px-3 py-2 rounded-lg border border-border/50 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30">
                      <option value="YYYY-MM-DD">2026-07-25</option><option value="DD/MM/YYYY">25/07/2026</option><option value="MM/DD/YYYY">07/25/2026</option>
                    </select>
                  </div>
                </div>
              )}

              {/* === 登录设备 === */}
              {activeTab === "sessions" && (
                <div className="space-y-4">
                  <h3 className="text-sm font-medium">当前活跃会话</h3>
                  <div className="p-4 rounded-lg border border-border/50">
                    <div className="flex items-center gap-3">
                      <Monitor className="w-8 h-8 text-[#2563EB]" />
                      <div className="flex-1">
                        <p className="text-sm font-medium">当前设备</p>
                        <p className="text-xs text-muted-foreground">浏览器 · {typeof navigator !== "undefined" ? navigator.userAgent.split(" ").pop() : "Unknown"}</p>
                      </div>
                      <Badge className="bg-green-100 text-green-700 border-green-200">当前</Badge>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">为保障账户安全，发现异常登录请及时修改密码。</p>
                </div>
              )}
            </>}
          </div>
          {/* Bottom action bar */}
          {(activeTab === "profile" || activeTab === "appearance") && (
            <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-border/50 bg-gray-50/50 dark:bg-slate-800/50">
              <Button variant="outline" size="sm" onClick={onClose}>取消</Button>
              {activeTab === "profile" && (
                <Button size="sm" className="bg-[#2563EB] hover:bg-[#2563EB]/90 text-white" onClick={saveProfile} disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}<Save className="w-4 h-4 mr-1" />保存设置
                </Button>
              )}
              {activeTab === "appearance" && (
                <Button size="sm" className="bg-[#2563EB] hover:bg-[#2563EB]/90 text-white" onClick={saveAppearance}>
                  <Save className="w-4 h-4 mr-1" />保存外观
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  , document.body);
}
